import { useState, useMemo } from 'react';
import { Conversation } from '../types/Conversation';
import {
  matchesBillingFilter,
  needsCallback,
  type ConversationBillingFilter,
} from '../utils/conversationBilling';
import type { Contact } from '../types/Contact';
import { normalizeForSearch } from '../utils/searchText';

type DateRange = {
  from: Date | undefined;
  to: Date | undefined;
};

type Filters = {
  dateRange: DateRange;
  billing: ConversationBillingFilter;
  callback: boolean;
  search: string;
};

type SortConfig = {
  key: keyof Conversation;
  subKey?: 'length' | 'count';
  direction: 'ascending' | 'descending';
};

interface UseConversationsOptions {
  /** Resolved contact names, so a search can match "Anna" and not just a number. */
  contacts?: Contact[];
}

export function useConversations(
  conversations: Conversation[],
  filters: Filters,
  { contacts = [] }: UseConversationsOptions = {}
) {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'created_at',
    direction: 'descending',
  });

  const contactNameByCaller = useMemo(() => {
    const map = new Map<string, string>();
    for (const contact of contacts) {
      if (contact.caller_id && contact.name) {
        map.set(contact.caller_id, normalizeForSearch(contact.name));
      }
    }
    return map;
  }, [contacts]);

  // Apply filters and sorting
  const filteredAndSortedConversations = useMemo(() => {
    const term = normalizeForSearch(filters.search);

    // First apply filters
    const result = conversations.filter(conversation => {
      const createdAt = new Date(conversation.created_at);

      // Check date range
      if (filters.dateRange.from) {
        const fromDate = new Date(filters.dateRange.from);
        fromDate.setHours(0, 0, 0, 0);
        if (createdAt < fromDate) {
          return false;
        }
      }

      if (filters.dateRange.to) {
        const toDate = new Date(filters.dateRange.to);
        toDate.setHours(23, 59, 59, 999);
        if (createdAt > toDate) {
          return false;
        }
      }

      if (!matchesBillingFilter(conversation, filters.billing)) {
        return false;
      }

      if (filters.callback && !needsCallback(conversation)) {
        return false;
      }

      if (term) {
        // What a search is for here is finding a call by something that was
        // said in it, so the transcript is the primary target; the number,
        // the contact's name and the summary come along because they cost
        // nothing and are what someone reaches for next.
        const haystacks = [
          conversation.caller_id,
          contactNameByCaller.get(conversation.caller_id),
          conversation.summary,
        ];

        const matchesMeta = haystacks.some(
          value => value && normalizeForSearch(value).includes(term)
        );

        if (!matchesMeta) {
          const matchesTranscript = (conversation.messages ?? []).some(
            message =>
              message.content &&
              normalizeForSearch(message.content).includes(term)
          );
          if (!matchesTranscript) return false;
        }
      }

      return true;
    });

    // Then apply sorting
    result.sort((a, b) => {
      if (sortConfig.key === 'messages') {
        if (sortConfig.subKey === 'length') {
          const lengthA = a.duration || 0;
          const lengthB = b.duration || 0;
          return sortConfig.direction === 'ascending'
            ? lengthA - lengthB
            : lengthB - lengthA;
        } else {
          const countA = a.messages?.length || 0;
          const countB = b.messages?.length || 0;
          return sortConfig.direction === 'ascending'
            ? countA - countB
            : countB - countA;
        }
      }

      const valueA = a[sortConfig.key];
      const valueB = b[sortConfig.key];

      if (
        valueA === undefined ||
        valueA === null ||
        valueB === undefined ||
        valueB === null
      )
        return 0;

      if (valueA < valueB) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (valueA > valueB) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });

    return result;
  }, [conversations, filters, sortConfig, contactNameByCaller]);

  const requestSort = (
    key: keyof Conversation,
    subKey?: 'length' | 'count'
  ) => {
    const direction =
      sortConfig && sortConfig.key === key && sortConfig.subKey === subKey
        ? sortConfig.direction === 'ascending'
          ? 'descending'
          : 'ascending'
        : 'ascending';
    setSortConfig({ key, subKey, direction });
  };

  return {
    conversations: filteredAndSortedConversations,
    requestSort,
  };
}
